import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "Website Updates",
    description:
      "Core updates to keep the website current, stable, and functioning properly.",
  },
  {
    icon: Layers,
    title: "Security Monitoring",
    description:
      "Checks and support to reduce risk and keep the site protected.",
  },
  {
    icon: Code,
    title: "Bug Fixes",
    description:
      "Ongoing issue resolution for broken elements, layout problems, or unexpected behavior.",
  },
  {
    icon: TrendingUp,
    title: "Performance Support",
    description:
      "Monitoring and improvements that help the site load and perform better.",
  },
  {
    icon: Smartphone,
    title: "Content and Page Updates",
    description:
      "Support for updating existing pages, adding small changes, and keeping the website current.",
  },
  {
    icon: Zap,
    title: "Technical Support",
    description:
      "Hands-on help when the website needs attention, troubleshooting, or follow-up fixes.",
  },
];

const offerings = [
  {
    title: "Routine Website Maintenance",
    description:
      "Ongoing support for updates, stability, and day-to-day website health.",
  },
  {
    title: "Technical Troubleshooting",
    description:
      "Help with site issues, broken functionality, and technical problems that need attention.",
  },
  {
    title: "Performance & Stability Support",
    description:
      "Maintenance focused on keeping the website fast, reliable, and usable.",
  },
  {
    title: "Post-Launch Support",
    description:
      "Support after launch to help the website stay aligned with business needs over time.",
  },
];

const deliverables = [
  "Regular update support",
  "Bug fixes and technical issue handling",
  "Basic performance monitoring",
  "Security-focused maintenance",
  "Page and content update support",
  "Troubleshooting when issues appear",
  "Technical review where needed",
  "Clear next-step recommendations",
];

const processSteps = [
  {
    step: "01",
    title: "Review",
    description:
      "We look at the current website setup, recent issues, and the kind of support the business actually needs.",
  },
  {
    step: "02",
    title: "Prioritize",
    description:
      "We identify which updates, fixes, or risks should be handled first.",
  },
  {
    step: "03",
    title: "Maintain",
    description:
      "We apply the needed updates, fixes, and improvements with a structured approach.",
  },
  {
    step: "04",
    title: "Monitor",
    description:
      "We keep an eye on performance, functionality, and technical health over time.",
  },
  {
    step: "05",
    title: "Support",
    description:
      "We respond to issues, content changes, and ongoing support needs as they come up.",
  },
  {
    step: "06",
    title: "Improve",
    description:
      "Where needed, we recommend improvements that support stronger performance and stability.",
  },
];

const faqs = [
  {
    question: "What is included in website maintenance?",
    answer: (
      <>
        Website maintenance can include updates, bug fixes, security support,
        performance checks, troubleshooting, and general technical upkeep
        depending on what the website needs.
      </>
    ),
    schemaAnswer:
      "Website maintenance can include updates, bug fixes, security support, performance checks, troubleshooting, and general technical upkeep depending on what the website needs.",
  },
  {
    question: "How often should a website be maintained?",
    answer: (
      <>
        A website should be reviewed regularly to keep updates current, reduce
        risk, and prevent avoidable technical issues from building up.
      </>
    ),
    schemaAnswer:
      "A website should be reviewed regularly to keep updates current, reduce risk, and prevent avoidable technical issues from building up.",
  },
  {
    question: "Can you maintain a website you did not build?",
    answer: (
      <>
        Yes. We can review an{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          existing website
        </Link>{" "}
        and support it based on its condition, setup, and maintenance
        requirements.
      </>
    ),
    schemaAnswer:
      "Yes. We can review an existing website and support it based on its condition, setup, and maintenance requirements.",
  },
  {
    question: "Do you also handle content updates?",
    answer: (
      <>
        Yes. We can support page updates, text changes, and smaller{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content-related adjustments
        </Link>{" "}
        as part of ongoing website support.
      </>
    ),
    schemaAnswer:
      "Yes. We can support page updates, text changes, and smaller content-related adjustments as part of ongoing website support.",
  },
  {
    question: "Can maintenance help with SEO and performance?",
    answer: (
      <>
        Yes. Website maintenance can support{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO and usability
        </Link>{" "}
        by helping keep the site fast, stable, and technically healthy.
      </>
    ),
    schemaAnswer:
      "Yes. Website maintenance can support SEO and usability by helping keep the site fast, stable, and technically healthy.",
  },
  {
    question: "What happens if my website has a sudden issue?",
    answer: (
      <>
        We can help troubleshoot the issue, identify the cause, and apply the
        fix needed to get the website working properly again.
      </>
    ),
    schemaAnswer:
      "We can help troubleshoot the issue, identify the cause, and apply the fix needed to get the website working properly again.",
  },
];

const whyChooseUs = [
  {
    title: "Practical Ongoing Support",
    description:
      "We focus on the updates, fixes, and improvements that actually keep the website working well.",
  },
  {
    title: "Clear Technical Oversight",
    description:
      "We monitor the site with a structured approach instead of waiting for problems to become obvious.",
  },
  {
    title: "Built for Business Continuity",
    description:
      "Support work helps reduce disruption and keeps the website stable as the business grows.",
  },
  {
    title: "Connected to Your Wider Setup",
    description:
      "Website support works best when it considers SEO, structure, content, and future development needs too.",
  },
];

export default function MaintenanceSupportPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
     <SEO
  title="Website Maintenance Support in Vancouver | Square Trigger"
  description="Get best-in-class Website support and maintenance services for optimal performance to provide 99.9% uptime of your Website. Request a Maintenance Quote now."
  ogTitle="Website Maintenance Support in Vancouver | Square Trigger"
  ogDescription="Get best-in-class Website support and maintenance services for optimal performance to provide 99.9% uptime of your Website. Request a Maintenance Quote now."
  ogSiteName="Square Trigger Website Maintenance Support"
  ogImage="https://squaretrigger.com/maintance.webp"
  keywords={SEO_KEYWORDS.maintenanceSupport}
/>

    {/* Hero Section */}
<section className="relative overflow-hidden">
  
  <div
    className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
    style={{ backgroundImage: "url('/maintance.webp')" }}
  />

  {/* Overlay for mobile + desktop */}
  <div className="absolute inset-0 bg-gradient-to-r from-foreground/72 via-foreground/58 to-foreground/38 lg:bg-gradient-to-br lg:from-foreground lg:via-foreground/95 lg:to-foreground/90" />

  <div className="absolute inset-0">
          <div className="absolute top-16 right-6 h-72 w-72 rounded-full bg-primary/20 blur-3xl sm:top-20 sm:right-20 sm:h-96 sm:w-96" />
          <div className="absolute bottom-16 left-6 h-56 w-56 rounded-full bg-secondary/20 blur-3xl sm:bottom-20 sm:left-20 sm:h-72 sm:w-72" />
        </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm font-medium">Our Services</span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Website Maintenance & Support in{" "}
                <span className="gradient-text">Vancouver</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-3 max-w-[640px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We provide website maintenance and support services that help
                businesses keep their website secure, updated, fast, and working
                properly after launch.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="mx-auto m-0 mb-6 max-w-[640px] text-[0.95rem] italic text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] mx-auto lg:mx-0"
              >
                We help you manage updates, fix issues faster, and keep your website
                performing reliably so your business stays online without unnecessary
                disruptions.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Contact Us
                  </Link>
                </Button>
              </motion.div>
            </div>

           <div className="hidden min-h-[420px] items-center justify-center lg:flex">
  <motion.div
    initial={{ opacity: 0, x: 30 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.8, delay: 0.4 }}
    className="relative flex items-center justify-center"
  >
    
    <div className="relative group">
      <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
      <img 
        src="/maintance.webp" 
        alt="Digital Agency Vancouver" 
        className="relative rounded-2xl shadow-2xl border border-white/10 object-cover w-full max-w-[520px] h-auto"
      />
    </div>
  </motion.div>
</div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
              A Website Needs Ongoing Support to Keep{" "}
              <span className="gradient-text">Performing Well</span>
            </h2>

            <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              A{" "}
              <Link
                to="/services/website-development"
                className="text-primary hover:underline"
              >
                website
              </Link>{" "}
              is not finished the moment it goes live. Over time, updates,
              fixes, monitoring, and performance checks are needed to keep
              everything secure, stable, and easy to use.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8"
            >
              <p className="mx-auto mb-6 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Website maintenance helps prevent avoidable problems, supports a
                better user experience, and gives your business more confidence
                that the website is working properly in the background.
              </p>

              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
                {[
                  {
                    title: "Security updates",
                    desc: "Regular updates help reduce risk and keep the website more secure.",
                  },
                  {
                    title: "Performance checks",
                    desc: "Monitoring and maintenance help keep the website loading and functioning properly.",
                  },
                  {
                    title: "Bug fixes",
                    desc: "Small issues can quickly affect trust, usability, and conversion if they are not handled.",
                  },
                  {
                    title: "Content and system updates",
                    desc: "A supported website is easier to keep current as the business changes.",
                  },
                  {
                    title: "Long-term stability",
                    desc: "Good maintenance supports the long-term value of the website instead of letting problems build up.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center transition-shadow hover:shadow-medium"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                      {item.title === "Bug fixes" ? (
                        <>
                          Small issues can quickly affect trust, usability, and{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            conversion
                          </Link>{" "}
                          if they are not handled.
                        </>
                      ) : item.title === "Content and system updates" ? (
                        <>
                          A supported website is easier to keep{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>{" "}
                          current as the business changes.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-14 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-10 text-center"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              Why Choose Square Trigger for Website Maintenance & Support
            </h2>
            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              We do not treat maintenance like an afterthought. Ongoing support
              is part of keeping a website useful, reliable, and aligned with
              the needs of the business over time.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-border bg-card p-6 text-center shadow-soft transition-shadow hover:shadow-medium sm:items-start sm:text-left"
              >
                <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                  <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="mb-2 font-semibold text-foreground">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {item.title === "Connected to Your Wider Setup" ? (
                    <>
                      Website support works best when it considers{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        SEO
                      </Link>
                      , structure,{" "}
                      <Link
                        to="/services/social-media-marketing"
                        className="text-primary hover:underline"
                      >
                        content
                      </Link>
                      , and future{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        development
                      </Link>{" "}
                      needs too.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-14"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Capabilities
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              What Our Maintenance & Support Service{" "}
              <span className="gradient-text">Includes</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:items-start sm:text-left">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl gradient-primary sm:mx-0">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                      {capability.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {capability.title === "Performance Support" ? (
                        <>
                          Monitoring and improvements that help the site load
                          and perform better, with broader performance support
                          that can also connect to{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>
                          .
                        </>
                      ) : capability.title === "Content and Page Updates" ? (
                        <>
                          Support for updating existing pages, adding small
                          changes, and keeping the website current with{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>{" "}
                          or page updates where needed.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Solutions
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              Support Built Around{" "}
              <span className="gradient-text">Ongoing Website Needs</span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex flex-col items-center rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:p-8 sm:text-left"
              >
                <h3 className="mb-3 text-xl font-display font-bold sm:text-2xl">
                  {offering.title === "Post-Launch Support" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="leading-relaxed text-background/70">
                  {offering.title === "Performance & Stability Support" ? (
                    <>
                      Maintenance focused on keeping the website fast, reliable,
                      and usable, with support that can also strengthen{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        performance
                      </Link>{" "}
                      where natural.
                    </>
                  ) : (
                    offering.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium uppercase tracking-wider text-primary">
                Deliverables
              </span>

              <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="mx-auto mb-8 max-w-[720px] text-base leading-relaxed text-muted-foreground sm:text-lg lg:mx-0">
                Our maintenance and support service is built around keeping your
                website working properly, reducing avoidable issues, and helping
                the business stay current online.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft sm:p-8 sm:text-left"
            >
              <ul className="inline-grid space-y-4 text-left sm:grid">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex w-fit items-start gap-3"
                  >
                    <CheckCircle className="mt-[2px] h-5 w-5 flex-shrink-0 text-primary" />
                    <span className="text-sm leading-relaxed text-foreground/90 sm:text-base">
                      {item === "Basic performance monitoring" ? (
                        <>
                          Basic{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            performance monitoring
                          </Link>
                        </>
                      ) : item === "Page and content update support" ? (
                        <>
                          Page and{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content update support
                          </Link>
                        </>
                      ) : item === "Technical review where needed" ? (
                        <>
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            Technical review
                          </Link>{" "}
                          where needed
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-12 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center sm:mb-16"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Process
            </span>
            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              How We Approach{" "}
              <span className="gradient-text">
                Website Maintenance & Support
              </span>
            </h2>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="card-hover flex h-full flex-col items-center border-border p-6 text-center transition-colors hover:border-primary/30 sm:items-start sm:text-left">
                  <div className="mb-4 flex w-full justify-center">
                    <div className="text-center text-3xl font-display font-bold gradient-text">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="mb-2 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step.title === "Monitor" ? (
                      <>
                        We keep an eye on{" "}
                        <Link
                          to="/services/seo-services"
                          className="text-primary hover:underline"
                        >
                          performance
                        </Link>
                        , functionality, and technical health over time.
                      </>
                    ) : step.title === "Support" ? (
                      <>
                        We respond to issues,{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content changes
                        </Link>
                        , and ongoing support needs as they come up.
                      </>
                    ) : step.title === "Improve" ? (
                      <>
                        Where needed, we recommend{" "}
                        <Link
                          to="/services/website-development"
                          className="text-primary hover:underline"
                        >
                          improvements
                        </Link>{" "}
                        that support stronger performance and stability.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Need Reliable Support for Your Website?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Let&apos;s look at what your website needs to stay secure, stable,
                and performing properly over time.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                Website Maintenance & Support –{" "}
                <span className="gradient-text">Frequently Asked Questions</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.schemaAnswer,
                  },
                })),
              })}
            </script>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Ready to Keep Your Website Running Properly?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let&apos;s put the right maintenance and support in place so your
                website stays secure, current, and reliable.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="gradient"
                  size="lg"
                  asChild
                  className="group w-full justify-center sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center"
                  >
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}